import { Component, OnDestroy, Output } from '@angular/core';
import { NavController } from '@ionic/angular';
import { RtsVehicle } from 'src/app/dto/get-rts-result.dto';
import { HeaderDto } from 'src/app/dto/header.dto';
import { Order } from 'src/app/dto/order.dto';
import { OrderChangesService } from 'src/app/services/order/order-changes.service';
import { Subject, takeUntil } from 'rxjs';
import { CarEditorData } from '../../../../shared/car-editor/car-editor.component';

@Component({
  selector: 'app-new-car',
  templateUrl: './new-car.component.html',
  styleUrls: ['./new-car.component.scss']
})
/**
 * Экран добавления новой машины (рейса) в день локально — на случай, когда логистическая компания ещё
 * не предоставила РТС или нужен дополнительный рейс. Машине присваивается уникальный в пределах дня
 * локальный id ({@link #addNewCar}), она добавляется в рабочую копию заказа и уходит в очередь
 * синхронизации через {@code updateRequests}.
 */
export class NewCarComponent implements OnDestroy {
  @Output() editFormSubmitted?: RtsVehicle;

  requestNumber: string = '';
  requestDayId: string = '';
  currentActionRequest: Order;
  targetUrl = window.location.pathname.split('/')[1];

  headerDataValues: HeaderDto = {
    title: 'Новая машина'
  };

  private readonly destroy$ = new Subject<void>();

  constructor(
    private orderChangesService: OrderChangesService,
    public navCtrl: NavController
  ) {
    this.requestNumber =
      window.location.pathname.split('/')[
        window.location.pathname.split('/').findIndex(el => el === 'days') + 1
      ];

    this.requestDayId =
      window.location.pathname.split('/')[
        window.location.pathname.split('/').findIndex(el => el === 'day') + 1
      ];

    // Подписываемся ОДИН раз: прежде подписка создавалась внутри addNewCar на каждый вызов и жила до
    // уничтожения компонента (накопление подписок, многократная реакция). BehaviorSubject эмитит
    // текущее значение синхронно при подписке, поэтому currentActionRequest готов к моменту addNewCar.
    this.orderChangesService
      .getOrderSubject(this.targetUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => (this.currentActionRequest = r!));
  }

  /**
   * Добавляет заполненную машину в текущий день заказа. Локальный id — {@code max(числовых id дня) + 1}
   * (почему только по числовым — см. комментарий ниже). Публикует правку и возвращается назад.
   */
  addNewCar(formData: Partial<CarEditorData>) {
    if (!this.currentActionRequest) {
      return;
    }

    // Уникальный локальный id в рамках дня — последовательная нумерация ТОЛЬКО по числовым id.
    // Серверные машины могут иметь нечисловые id (parseInt → NaN); прежде Math.max с NaN давал id 'NaN',
    // что ломало поиск по id и приводило к коллизиям.
    const currentDay = this.currentActionRequest.getDayById(this.requestDayId);
    let newId = 1;
    if (currentDay && currentDay.vehicles.length > 0) {
      const numericIds = currentDay.vehicles
        .map(v => parseInt(v.id, 10))
        .filter(n => !Number.isNaN(n));
      if (numericIds.length > 0) {
        newId = Math.max(...numericIds) + 1;
      }
    }

    const vehicle: Partial<RtsVehicle> = {
      ...formData,
      id: newId.toString(),
      hasTrailer: !!formData.trailerRegistrationNumber
    };

    this.orderChangesService.updateRequests(
      this.currentActionRequest.addVehicle(
        this.requestDayId,
        vehicle as RtsVehicle
      ),
      this.targetUrl
    );

    this.navCtrl.pop();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
